import moment from 'moment';
import React from 'react';

import { CacheableDataObject } from 'models/CacheableDataObject.jsx';
import { Request } from 'components/Request.jsx';
import { Dashboard } from 'components/Dashboard.jsx';
import { Users } from 'models/UserCollection';
import { Character } from 'models/Character.jsx';

import { LadderLinker } from 'components/LadderLinker.jsx';
import { LadderTime } from 'components/LadderTime.jsx';
import { DateFormat as DateFormats } from '../components/DateFormat';
import { ChatActions } from '../components/ChatActions';
import { setConnectCodeNotice } from 'react/match/room/connectCodeNotice';
import { showToast } from 'react/toasts/showToast';

class ChatMessage extends CacheableDataObject {
	static instanceIds = 1;

	// Prefix unique to this page load, so send ids never collide with another tab of the same
	// user: the socket echoes every send to all of that user's tabs, and each tab reconciles
	// its pending messages by send_id.
	static sendIdPrefix = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
	static sendIdCounter = 0;

	static generateSendId() {
		return `${ChatMessage.sendIdPrefix}-${++ChatMessage.sendIdCounter}`;
	}

	static activeSendPromises = {};
	static idTypes = {
		DB_ID: 'DB_ID',
		SEND_ID: 'SEND_ID',
	};

	/** Persisted message kinds drawn as cards (`kind` in the message payload). */
	static kinds = {
		LFG: 'lfg',
		MATCH_RESULT: 'match_result',
		MODERATOR_EVENT: 'moderator_event',
	};

	/** An LFG card's `kind_data.mode`. */
	static lfgModes = {
		FRIENDLIES: 'friendlies',
		RANKED: 'ranked',
		DOUBLES: 'doubles',
	};

	/** An LFG card's `kind_data.status`. open and challenged can still change. */
	static lfgStatuses = {
		OPEN: 'open',
		CHALLENGED: 'challenged',
		TAKEN: 'taken',
		CANCELLED: 'cancelled',
		EXPIRED: 'expired',
	};

	beforeConstruct() {
		this.cacheIndex = null; //Used for display updating
		this.usedToScrollToBottom = false;
		this.message = '';
		this.isNew = null;
		this.notification = null;
		this.type = ChatMessageNotification.types.CHAT_MESSAGE;
		this.instanceId = ChatMessage.instanceIds++;
		this.messageParser = null;
		this.reactions = [];
		this.senderMain = null;
		this.kind = null;
		this.kindData = null;
		this.lfgRequest = null; // chat/post_lfg parameters of a card this tab is posting
		this.lfgChallengeSent = false; // this tab challenged the card's search
		this.lfgChallengeSeenInFeed = false; // that challenge has appeared in the searches feed
		this.lfgChallengeError = null;
		this.sendPromise = null; // the chat/post_lfg request of a card this tab is posting
	}

	afterConstruct() {
		this.sendData = null;
		this.previousMessageHasSameOwner = null;
		this.chatMessages = null;
	}

	afterUpdate() {
		if(!this.time) {
			this.time = LadderTime.getEstimatedServerTime();
		}
	}

	removeFromChat() {
		if(!this.chatMessages) {
			return;
		}
		this.chatMessages.removeMessage(this);
	}

	setChatMessages(chatMessages) {
		this.chatMessages = chatMessages;
	}

	getListKey() {
		if(this.listKey) {
			return this.listKey; // Carried over from the pending copy so React keeps the same element on confirm.
		}
		return this.id ? this.id : 'send_id' + this.send_id;
	}

	getContext() {
		return {
			player_id: this.player.id,
			chat_room_id: this.chat_room_id,
			message_id: this.id,
			match_id: this.match_id
		};
	}

	addDeleteHighlight() {
		this.chatMessages.addDeleteHighlight(this);
	}

	removeDeleteHighlight() {
		this.chatMessages.removeDeleteHighlight(this);
	}

	updateState(fields) {
		for(let i in fields) {
			if(!fields.hasOwnProperty(i)) {
				continue;
			}
			if(!this.hasOwnProperty(i)) {
				throw new Error('attempt to set property ' + i + ' when it is not a property');
			}
			this[i] = fields[i];
		}
		if(this.component) {
			console.log('calling force update?', this.component);
			this.component.forceUpdate();
		}
		else {
			console.log('had no reference to force update');
		}
	}

	addSendFailedReason(type, reason) {
		this.sendFailedReason = reason;
	}

	processError(response) {
		if(response.rate_limit == true) {
			this.addSendFailedReason(ChatMessage.SEND_FAIL_RATE_LIMIT, 'Your last message was not sent, you are sending messages too quickly.');
		}
		if(response.error) {
			this.addSendFailedReason(ChatMessage.SEND_FAIL_SERVER_ERROR, response.error);
		}
		this.sendFailed = true;
	}

	setComponent(component) {
		this.component = component;
	}

	deleteMessage() {
		this._deleteMessageCommon('delete');
	}

	removeMessage() {
		this._deleteMessageCommon('remove');
	}

	/** The sender's main for this room's game (a Character), or null. */
	getSenderMain() {
		return this.senderMain || null;
	}

	getSenderStockIconUrl() {
		return (this.senderMain && this.senderMain.stock_icon_url) || null;
	}

	isRemovedInSomeWay() {
		return this.deleted || this.is_shadow_muted;
	}

	revealMessage() {
		Request.apiv1('chat/unremove', { message_id: this.id })
			.then((response) => {
				Dashboard.performOpenSearchUpdate(response);
			});
	}

	undeleteMessage() {
		if(this.isModOfMessage) {
			//activeDeleteButton.add($element.find('.time'));
			if(!this.deleted) {
				console.error('message is not deleted');
				return;
			}
			this.isUpdating = true;

			Request.apiv1('chat/undelete', { message_id: this.id }).then((response) => {
				this.isUpdating = false;
				Dashboard.performOpenSearchUpdate(response);
				return true;
			});
		}
	}

	isSelfDeleted() {
		if(this.player && this.player.id === this.deleted_by) {
			return true;
		}
		return false;
	}

	// An LFG card its poster deleted, or that a later card of theirs replaced:
	// it leaves no trace in the room. A mod-deleted card still shows as deleted.
	isSelfDeletedLfgCard() {
		return !!this.deleted && this.kind === ChatMessage.kinds.LFG && this.isSelfDeleted();
	}

	isModDeleted() {
		if(this.player && this.player.id !== this.deleted_by) {
			return true;
		}
		return false;
	}

	isChatMod() {
		return this.is_chat_mod || this.is_chat_admin;
	}

	isMyMessage() {
		return this.player && this.player.isMe();
	}

	/** Your own ordinary messages; cards are not text you can edit. */
	canBeEdited() {
		return !!this.isMyMessage() && !this.kind;
	}

	get isModOfMessage() {
		const player = myUser;
		if(!this.chatMessages) {
			return false;
		}
		return this.chatMessages.hasMod(player);
	}

	_deleteMessageCommon(type) {
		if(!type) {
			throw new Error('type is required');
		}
		const types = {
			remove: 1,
			delete: 1
		};
		if(!types.hasOwnProperty(type)) {
			throw new Error('invalid type');
		}
		if((this.player.isMe() || this.isModOfMessage)) {
			if(type === 'remove') {
				if(this.is_shadow_muted) {
					return;
				}
			}
			else {
				if(this.deleted) {
					return;
				}
			}
			this.isUpdating = true;
			let action = type === 'remove' ? 'chat/remove' : 'chat/delete';
			const previousDeletedState = { deleted: this.deleted, deleted_by: this.deleted_by };
			if(type === 'delete') {
				// Show the deletion right away; the server payload confirms it, a failure restores it.
				this.deleted = true;
				this.deleted_by = myUser.id;
			}
			this.changed();
			const finished = (response) => {
				this.isUpdating = false;
				Dashboard.performOpenSearchUpdate(response);
				this.changed();
			};
			Request.apiv1(action, { message_id: this.id }).then((response) => {
				finished(response);
			}, () => {
				this.deleted = previousDeletedState.deleted;
				this.deleted_by = previousDeletedState.deleted_by;
				finished();
			});
		}
		else {
			showToast({ kind: 'warning', content: 'You can\'t delete this message' });
		}
	}

	async send() {
		this.sending = true;

		if(!this.send_id) {
			this.send_id = ChatMessage.generateSendId();
		}
		const postsLfgCard = !!this.lfgRequest;
		this.sendData = postsLfgCard ? { ...this.lfgRequest, send_id: this.send_id } : {
			chat_room_id: this.chatMessages.chatRoomId,
			to_user_id: this.chatMessages.player ? this.chatMessages.player.id : null,
			match_id: this.chatMessages.matchId,
			send_id: this.send_id,
			message: this.message,
			premade_response_id: this.responseId,
		};

		this.sendFailed = false;
		this.sendFailedReason = null;
		this.chatMessages.addMessage(this, true);

		this.ladderTime = LadderTime.sentRequest();
		this.changed();
		const sendId = this.send_id;
		return ChatMessage.activeSendPromises[sendId] = Request.apiv1(postsLfgCard ? 'chat/post_lfg' : 'chat/send', this.sendData)
			.then((response) => {
				if(response.success === false) {
					this.processError(response);
				}
				else if(response.connect_code_applied) {
					setConnectCodeNotice(response.connect_code_applied.match_id, response.connect_code_applied);
				}
				Dashboard.performOpenSearchUpdate(response);
				return response.message_id;
			})
			.catch((response) => {
				// A network failure has no JSON body; it still has to surface the retry state.
				this.processError((response && response.responseJSON) || {});
				this.changed();
			})
			.finally(() => {
				delete ChatMessage.activeSendPromises[sendId];
			});
	}

	isStateMessage() {
		if(this.hasOwnProperty('state_message')) {
			return true;
		}
		return false;
	}

	/**
	 * Whether the current user has reacted to this message with the given emoji.
	 * Derived from the reactor id list so the same payload is correct for everyone.
	 */
	hasMyReaction(emoji) {
		const entry = this.reactions.find((reaction) => reaction.emoji === emoji);
		return !!(entry && entry.player_ids.indexOf(myUser.id) !== -1);
	}

	/**
	 * Toggle the current user's reaction with `emoji`, optimistically updating the
	 * local reaction list before the request resolves. The server echoes the
	 * authoritative reaction set (both in the response and over the socket), which
	 * replaces the optimistic state so it self-heals. If the request fails, the
	 * previous reactions are restored unless a newer set has arrived meanwhile.
	 */
	toggleReaction(emoji) {
		if(!this.id) {
			return; // A message that has not been persisted yet cannot carry reactions.
		}
		const myId = myUser.id;
		const previousReactions = this.reactions;
		const entry = previousReactions.find((reaction) => reaction.emoji === emoji);
		if(entry && entry.player_ids.indexOf(myId) !== -1) {
			const remainingPlayerIds = entry.player_ids.filter((id) => id !== myId);
			this.reactions = remainingPlayerIds.length
				? previousReactions.map((reaction) => (reaction === entry ? { ...entry, count: remainingPlayerIds.length, player_ids: remainingPlayerIds } : reaction))
				: previousReactions.filter((reaction) => reaction !== entry);
		} else if(entry) {
			const playerIds = entry.player_ids.concat([myId]);
			this.reactions = previousReactions.map((reaction) => (reaction === entry ? { ...entry, count: playerIds.length, player_ids: playerIds } : reaction));
		} else {
			this.reactions = previousReactions.concat([{ emoji: emoji, count: 1, player_ids: [myId] }]);
		}
		const optimisticReactions = this.reactions;
		this.changed();

		const rollBack = () => {
			if(this.reactions === optimisticReactions) {
				this.reactions = previousReactions;
				this.changed();
			}
		};
		return Request.apiv1('chat/react', { message_id: this.id, emoji: emoji })
			.then((response) => {
				if(response && response.success && response.reactions) {
					this.reactions = response.reactions;
					this.changed();
				} else {
					rollBack();
				}
			}, rollBack);
	}

	changed() {
		this.chatMessages.updateDisplay();
	}

	/** 'lfg', 'match_result', 'moderator_event', or null for an ordinary message. */
	getKind() {
		return this.kind || null;
	}

	/**
	 * The LFG card data: match_id, player_id, ladder {id, name, slug}, mode
	 * (friendlies | ranked | doubles), best_of (null when endless), main, title,
	 * status (see lfgStatuses) and expires_at (unix seconds). null otherwise.
	 */
	getLfg() {
		return this.kind === ChatMessage.kinds.LFG ? this.kindData : null;
	}

	/**
	 * The posted result: match_id, ladder, mode, best_of, winner and loser
	 * ({id, username, main, score, players}; id and main are null for a doubles
	 * team) and finished_at. null otherwise.
	 */
	getMatchResult() {
		return this.kind === ChatMessage.kinds.MATCH_RESULT ? this.kindData : null;
	}

	/**
	 * A match chat's moderator event: event (asked | joined | left) and player
	 * {id, username}, who asked or the mod who joined or left. null otherwise.
	 */
	getModeratorEvent() {
		return this.kind === ChatMessage.kinds.MODERATOR_EVENT ? this.kindData : null;
	}

	/** Posted results and moderator events have no sender (no `player` in the payload). */
	isSystemMessage() {
		return this.kind === ChatMessage.kinds.MATCH_RESULT || this.kind === ChatMessage.kinds.MODERATOR_EVENT;
	}

	/**
	 * Apply a chat_message_kind_updates entry ({kind, status, expires_at}).
	 * Returns whether the message changed.
	 */
	applyKindUpdate(update) {
		if(!update || !this.kindData || update.kind !== this.kind || this.kind !== ChatMessage.kinds.LFG) {
			return false;
		}
		const previousStatus = this.kindData.status;
		this.kindData = { ...this.kindData, status: update.status, expires_at: update.expires_at };
		const declinedBackToOpen = previousStatus === ChatMessage.lfgStatuses.CHALLENGED && update.status === ChatMessage.lfgStatuses.OPEN;
		if(declinedBackToOpen || !this.isLfgOpen()) {
			this.resetLfgChallengeSent();
		}
		return true;
	}

	resetLfgChallengeSent() {
		this.lfgChallengeSent = false;
		this.lfgChallengeSeenInFeed = false;
	}

	/**
	 * While this tab's challenge to the card is pending, watch the searches feed
	 * for it (a challenge is a match whose parent is the card's search, with this
	 * player as player2). Once it has appeared and then leaves the feed, the
	 * poster declined or it ended: the card offers Challenge again. Returns
	 * whether the card changed.
	 */
	syncLfgChallengeWithSearchFeed(searchListManager) {
		const lfg = this.getLfg();
		if(!this.lfgChallengeSent || !lfg || !searchListManager) {
			return false;
		}
		const inFeed = ChatMessage.searchFeedHasMyChallengeTo(searchListManager, lfg.match_id);
		if(inFeed) {
			this.lfgChallengeSeenInFeed = true;
			return false;
		}
		if(!this.lfgChallengeSeenInFeed) {
			return false;
		}
		this.resetLfgChallengeSent();
		this.changed();
		return true;
	}

	static searchFeedHasMyChallengeTo(searchListManager, searchId) {
		for(const matchList of searchListManager.matchLists) {
			for(const match of matchList.matches.values()) {
				if(!match.is_removed && match.parent_match_id && String(match.parent_match_id) === String(searchId)
					&& match.player2 && match.player2.isMe()) {
					return true;
				}
			}
		}
		return false;
	}

	/** Whether the card's search can still be challenged (open, or challenged by someone else). */
	isLfgOpen() {
		const lfg = this.getLfg();
		return !!lfg && (lfg.status === ChatMessage.lfgStatuses.OPEN || lfg.status === ChatMessage.lfgStatuses.CHALLENGED);
	}

	/**
	 * Challenge the card's search. Optimistic: once the player commits (after
	 * the toxic warning, if any) the card shows the challenge as sent; a failed
	 * request rolls that back and keeps the error for the card to show.
	 */
	challengeLfg() {
		const lfg = this.getLfg();
		if(!this.isLfgOpen() || !lfg.match_id || !this.player) {
			return Promise.resolve();
		}
		this.lfgChallengeError = null;
		// Loaded on demand: the helper pulls in the store and matchmaking actions.
		const { confirmChallenge, sendChallenge, challengeErrorText, ChallengeDeclined } = require('components/matchmaking/ChallengeRequests');
		return confirmChallenge(this.player)
			.then(() => {
				this.lfgChallengeSent = true;
				this.lfgChallengeSeenInFeed = false;
				this.changed();
				return sendChallenge(this.player, lfg.match_id, { reportError: null })
					.catch((failure) => {
						this.resetLfgChallengeSent();
						this.lfgChallengeError = challengeErrorText(failure);
						this.changed();
					});
			}, (reason) => {
				if(!(reason instanceof ChallengeDeclined)) {
					this.lfgChallengeError = challengeErrorText(reason);
				}
				this.changed();
			});
	}

	hasMentionFor(player) {
		if(!player || !this.messageParser) {
			return false;
		}
		if(this.kind) {
			return false; // Cards name players in their text; that is not a mention.
		}
		if(this.player && this.player.id === player.id) {
			return false; // Naming yourself in your own message does not ping you.
		}
		return this.messageParser.usernamesFound[player.id];
	}

	isMuted() {
		return !!this.is_muted;
	}

	hasMuteTime() {
		return moment.isMoment(this.is_muted);
	}
}

ChatMessage.prototype.dataLocationParsers = {
	id: (message, data) => {
		message.id = String(data.id);
	},
	time: (message, data) => {
		message.time = DateFormats.momentFromLadderTime(data.time);
	},
	edited: (message, data) => {
		message.edited = DateFormats.momentFromLadderTime(data.edited);
	},
	is_muted: (message, data) => {
		if(data.is_muted === 0 || data.is_muted === 1) {
			message.is_muted = data.is_muted ? true : false;
			return;
		}
		message.is_muted = DateFormats.momentFromLadderTime(data.is_muted);
	},
	player: (message, data) => {
		message.player = Users.update(data.player);
		if(data.current_hit_points !== null && data.current_hit_points !== undefined) {
			message.player.hitPoints.setHealth(data.current_hit_points);
		}
	},
	send_id: (message, data) => {
		message.send_id = String(data.send_id);
	},
	message: (message, data) => {
		if(data.deleted === true && message.isModOfMessage) {
			return;
		}
		message.messageHtml = '';
		if(data.message === null) {
			message.message = '';
		}
		else {
			message.message = String(data.message);
			// Read here, not in its own parser: the chips need it while the text is parsed.
			message.linkedRooms = data.linked_rooms || null;
			const { messageReact, messageParser } = LadderLinker.reactAutolinkMessage(message);
			message.messageReact = messageReact;
			message.messageParser = messageParser;
		}
	},
	state_message: (message, data) => {
		message.state_message = data.state_message.typing == 1 ? true : false;
	},
	kind: (message, data) => {
		message.kind = data.kind || null;
	},
	kind_data: (message, data) => {
		message.kindData = data.kind_data ? { ...data.kind_data } : null;
	},
	sender_main: (message, data) => {
		message.senderMain = data.sender_main ? Character.create(data.sender_main) : null;
	},
	linked_rooms: () => {
		// Read by the message parser above.
	},
	reactions: (message, data) => {
		message.reactions = Array.isArray(data.reactions) ? data.reactions : [];
	}
};


class ChatMessageNotification extends ChatMessage {

	beforeConstruct() {
		super.beforeConstruct();
		this.type = ChatMessageNotification.types.TEXT;
		this.isNotification = true;

		this.summary;
		this.title;
		this.player;
		this.url;
	}

	afterConstruct() {
		if(!this.time) {
			this.time = moment();
		}
		super.afterConstruct();
	}

	postToMainChat() {
		ChatActions.addNotificationToChat(null, this);
	}

	static retrieveNotificationId() {
		return ChatMessageNotification.autoIncrement++;
	}

	get id() {
		if(!this._id) {
			this._id = ChatMessageNotification.retrieveNotificationId();
		}
		return 'notification_' + this._id;
	}

}

ChatMessageNotification.autoIncrement = 1;

ChatMessageNotification.types = {
	MOD_NOTIFICATION: 'MOD_NOTIFICATION',
	ROCK_PAPER_SCISSORS: 'ROCK_PAPER_SCISSORS',
	TEXT: 'TEXT',
	CHAT_RESTRICTION: 'CHAT_RESTRICTION',
	DIRECT_MESSAGE: 'DIRECT_MESSAGE',
	MATCH_MESSAGE: 'MATCH_MESSAGE',
	MULTIPLE_REPORTS: 'MULTIPLE_REPORTS',
	HOST_CODE: 'HOST_CODE',
	DATE_CHANGE: 'DATE_CHANGE',
	JOINED_MATCH: 'JOINED_MATCH',
	ACCEPTED_INTO_MATCH: 'ACCEPTED_INTO_MATCH',
	FRIEND_REQUEST: 'FRIEND_REQUEST',
	FRIEND_ONLINE: 'FRIEND_ONLINE',
	FRIEND_REQUEST_ACCEPTED: 'FRIEND_REQUEST_ACCEPTED',

	INVITE_PLAYER_NO_EXIST: 'INVITE_PLAYER_NO_EXIST',
	INVITE_NO_PERMISSION: 'INVITE_NO_PERMISSION',
	INVITE_ALREADY_INVITED: 'INVITE_ALREADY_INVITED',
	INVITE_IN_THERE: 'INVITE_IN_THERE',
	RPG_MESSAGE: 'RPG_MESSAGE',
	CHAT_MESSAGE: 'CHAT_MESSAGE',


	SELECT_CHARACTER: 'SELECT_CHARACTER',
	SELECT_STAGE: 'SELECT_STAGE',
	STAGE_STRIKE: 'STAGE_STRIKE',
	SUBMIT_RESULT: 'SUBMIT_RESULT',
	GAME_START: 'GAME_START',
};

export { ChatMessage, ChatMessageNotification };
